/**
 * The soul panel — variant A's left column (#42 §6).
 *
 * Header, version rail, the document (Charter · Voice · Questions I own ·
 * Rules you set), then the leash as a VISUALLY SEPARATED read-only block
 * with its floors marked locked, and — only when one exists — the agent's
 * own pending soul proposal.
 *
 * PROVENANCE IS PER FIELD. Every field says `this tenant` or `default`,
 * using the vocabulary step 1 already surfaces as
 * `AgentSettingsResponse.source`. A reader must never have to guess
 * whether a sentence came from their tenant or from the platform.
 *
 * Everything here is READ-ONLY in this step: editing is step 7's, and
 * approving the agent's proposal goes through the existing approvals
 * endpoints rather than a second write path invented here.
 */

import { Badge, BlockStack, Card, Text } from '@shopify/polaris';
import { Link } from 'react-router-dom';

import type { SoulResponse, SoulVersion } from '../../../types/agentMind';
import styles from '../workspace.module.css';

function ProvenanceTag({
  field,
  soul,
}: {
  field: string;
  soul: SoulResponse;
}) {
  const value = soul.provenance?.[field];
  if (!value) return null;
  return (
    <span className={styles.provenance} data-testid={`provenance-${field}`}>
      {value === 'this_tenant' ? 'this tenant' : 'default'}
    </span>
  );
}

function VersionRail({
  soul,
  versions,
}: {
  soul: SoulResponse;
  versions: SoulVersion[];
}) {
  // No delta ever recorded: one honest entry, not an empty rail.
  const rows =
    versions.length > 0
      ? versions
      : [
          {
            label: soul.current_version.label,
            at: soul.current_version.at,
            field: 'all',
            previous: null,
            new: null,
            attribution: soul.current_version.attribution ?? 'code-shipped',
            by: soul.current_version.by,
            rationale: null,
            source_proposal_id: null,
          } as SoulVersion,
        ];

  return (
    <div className={styles.versionRail} data-testid="version-rail">
      {rows.map((version, index) => (
        <div key={`${version.label}-${index}`} className={styles.versionRow}>
          <span className={styles.mono}>{version.label}</span>
          {' · '}
          {versions.length === 0
            ? 'default'
            : version.at
              ? new Date(version.at).toLocaleDateString()
              : 'undated'}
          {' · '}
          {versions.length === 0
            ? version.attribution
            : `${version.field}${
                version.previous && version.new
                  ? `: ${version.previous} → ${version.new}`
                  : ''
              }`}
          {versions.length > 0 && version.attribution ? (
            <> · {version.attribution}</>
          ) : null}
        </div>
      ))}
    </div>
  );
}

export function SoulPanel({
  soul,
  versions,
}: {
  soul: SoulResponse;
  versions: SoulVersion[];
}) {
  const locked = soul.leash.locked_fields ?? [];

  return (
    <Card>
      <BlockStack gap="300">
        <div data-testid="soul-panel">
          <Text as="h2" variant="headingMd">
            {`Soul · v${soul.current_version.label.replace(/^v/, '')} · who this agent is`}
          </Text>
          <div className={styles.subtle}>
            {soul.current_version.at
              ? `current since ${new Date(
                  soul.current_version.at
                ).toLocaleDateString()}`
              : 'current'}{' '}
            · template{' '}
            <span className={styles.mono}>
              {soul.template.key}@{String(soul.template.version)}
            </span>
          </div>
        </div>

        <VersionRail soul={soul} versions={versions} />

        <div>
          <Text as="h3" variant="headingSm">
            Charter
            <ProvenanceTag field="charter" soul={soul} />
          </Text>
          <Text as="p">{soul.charter}</Text>
        </div>

        <div>
          <Text as="h3" variant="headingSm">
            Voice
            <ProvenanceTag field="voice" soul={soul} />
          </Text>
          <Text as="p">{soul.voice}</Text>
        </div>

        <div>
          <Text as="h3" variant="headingSm">
            Questions I own
            <ProvenanceTag field="owned_questions" soul={soul} />
          </Text>
          {soul.owned_questions.length === 0 ? (
            <Text as="p" tone="subdued">
              None yet.
            </Text>
          ) : (
            <ul>
              {soul.owned_questions.map((question) => (
                <li key={question}>{question}</li>
              ))}
            </ul>
          )}
        </div>

        <div>
          <Text as="h3" variant="headingSm">
            Rules you set
            <ProvenanceTag field="soul_rules" soul={soul} />
          </Text>
          {soul.soul_rules.length === 0 ? (
            <Text as="p" tone="subdued">
              You have not added a rule yet.
            </Text>
          ) : (
            <ul>
              {soul.soul_rules.map((rule) => (
                <li key={rule}>{rule}</li>
              ))}
            </ul>
          )}
        </div>

        <div className={styles.leashBlock} data-testid="leash-block">
          <Text as="h3" variant="headingSm">
            What I may do · measure · own
          </Text>
          <div className={styles.subtle}>
            Read-only here. Platform floors cannot be crossed by any setting.
          </div>
          <ul>
            <li>
              Actions:{' '}
              <span className={styles.mono}>
                {soul.leash.allowed_action_types.join(', ') || 'none'}
              </span>
              {locked.includes('allowed_action_types') ? (
                <span
                  className={styles.provenance}
                  data-testid="leash-locked-allowed_action_types"
                >
                  locked
                </span>
              ) : null}
            </li>
            <li>
              Platforms:{' '}
              <span className={styles.mono}>
                {soul.leash.allowed_platforms.join(', ') || 'none'}
              </span>
              {locked.includes('allowed_platforms') ? (
                <span
                  className={styles.provenance}
                  data-testid="leash-locked-allowed_platforms"
                >
                  locked
                </span>
              ) : null}
            </li>
            {soul.leash.owned_tools?.length ? (
              <li>
                Tools:{' '}
                <span className={styles.mono}>
                  {soul.leash.owned_tools.join(', ')}
                </span>
              </li>
            ) : null}
          </ul>
        </div>

        {soul.pending_proposal ? (
          <div data-testid="soul-proposal-card">
            <Card>
              <BlockStack gap="200">
                <Badge tone="attention">
                  soul change · proposed by the agent · tier 2
                </Badge>
                <Text as="p" fontWeight="semibold">
                  {soul.pending_proposal.title}
                </Text>
                {soul.pending_proposal.rationale ? (
                  <Text as="p" tone="subdued">
                    {soul.pending_proposal.rationale}
                  </Text>
                ) : null}
                <Link
                  to={`/approvals?highlight=${soul.pending_proposal.proposal_id}`}
                >
                  Review it in approvals
                </Link>
              </BlockStack>
            </Card>
          </div>
        ) : null}
      </BlockStack>
    </Card>
  );
}
